import { Alert, Input } from "@arco-design/web-react";

import type { ItineraryNode, ItinerarySegment, RouteType, TransportMode } from "../types";
import {
  arrivalCalendarDate,
  calculatedRouteDurationMinutes,
  compactRouteTime,
  routeConstraintWarning,
  routeTypeModes,
  switchRouteTransportMode,
  transportModeLabels,
  withCalculatedRouteDuration,
  withEstimatedBusRouteTimes,
} from "./itineraryRouteDraft";

type Props = {
  draft: ItinerarySegment;
  fromNode: ItineraryNode | null;
  toNode: ItineraryNode | null;
  departureDayDate?: string | null;
  arrivalDayDate?: string | null;
  estimatedDurationMinutes?: number | null;
  durationEditable?: boolean;
  showConstraintWarning?: boolean;
  showTransportTypeFields?: boolean;
  onChange: (draft: ItinerarySegment) => void;
};

/** 交通工作区与兼容路线弹窗共用的人工路线字段。 */
export function ItineraryRouteFields({
  draft,
  fromNode,
  toNode,
  departureDayDate,
  arrivalDayDate,
  estimatedDurationMinutes,
  durationEditable = false,
  showConstraintWarning = true,
  showTransportTypeFields = true,
  onChange,
}: Props) {
  const warning = routeConstraintWarning(draft, fromNode, toNode);
  const durationIsCalculated = !durationEditable && calculatedRouteDurationMinutes(
    draft.departureAt,
    draft.arrivalAt,
  ) !== null;
  const showRouteCode = draft.transportMode === "rail" || draft.transportMode === "air";
  const showDistance = draft.transportMode !== "rail" && draft.transportMode !== "air";
  const candidateDurationMinutes = estimatedDurationMinutes ?? draft.durationMinutes;
  const anchoredDurationMinutes = ["bus", "walk"].includes(draft.transportMode)
    && Boolean(departureDayDate)
    && departureDayDate === arrivalDayDate
    && candidateDurationMinutes !== null
    && candidateDurationMinutes !== undefined
    && Number.isFinite(candidateDurationMinutes)
    && candidateDurationMinutes > 0
    ? candidateDurationMinutes
    : null;

  return (
    <div className="itinerary-dialog-form itinerary-dialog-form--route">
      {showTransportTypeFields ? <>
        <label htmlFor="itinerary-route-type">路线类型</label>
        <select
          id="itinerary-route-type"
          value={draft.routeType}
          onChange={(event) => {
            const routeType = event.target.value as RouteType;
            onChange({
              ...switchRouteTransportMode(draft, routeType, routeTypeModes[routeType][0]),
              sourceType: draft.sourceType,
            });
          }}
        >
          <option value="main_transport">大交通</option>
          <option value="charter_bus">包车</option>
          <option value="local_transfer">市内接驳</option>
        </select>
        <label htmlFor="itinerary-transport-mode">交通方式</label>
        <select
          id="itinerary-transport-mode"
          value={draft.transportMode}
          onChange={(event) => onChange(switchRouteTransportMode(
            draft,
            draft.routeType,
            event.target.value as TransportMode,
          ))}
        >
          {routeTypeModes[draft.routeType].map((mode) => (
            <option key={mode} value={mode}>{transportModeLabels[mode]}</option>
          ))}
        </select>
      </> : null}
      {showRouteCode ? <>
        <label htmlFor="itinerary-route-code">班次/车次</label>
        <Input
          id="itinerary-route-code"
          maxLength={100}
          value={draft.routeCode ?? ""}
          onChange={(value) => onChange({ ...draft, routeCode: value || null })}
        />
      </> : null}
      <label htmlFor="itinerary-departure-at">出发时刻</label>
      <input
        id="itinerary-departure-at"
        type="time"
        value={compactRouteTime(draft.departureAt)}
        onChange={(event) => {
          const departureTime = event.target.value;
          const arrivalTime = compactRouteTime(draft.arrivalAt);
          const departureAt = departureTime && departureDayDate
            ? `${departureDayDate}T${departureTime}:00`
            : null;
          const resolvedArrivalDate = arrivalCalendarDate(
            departureDayDate,
            arrivalDayDate,
            departureTime,
            arrivalTime,
          );
          const anchorDeparture = Boolean(departureAt && anchoredDurationMinutes !== null);
          const next = withCalculatedRouteDuration({
            ...draft,
            departureAt,
            arrivalAt: !anchorDeparture && arrivalTime && resolvedArrivalDate
              ? `${resolvedArrivalDate}T${arrivalTime}:00`
              : anchorDeparture
                ? null
                : draft.arrivalAt,
            durationMinutes: anchorDeparture ? anchoredDurationMinutes : draft.durationMinutes,
          });
          onChange(departureTime
            ? withEstimatedBusRouteTimes(next, departureDayDate, arrivalDayDate)
            : next);
        }}
      />
      <label htmlFor="itinerary-arrival-at">到达时刻</label>
      <input
        id="itinerary-arrival-at"
        type="time"
        value={compactRouteTime(draft.arrivalAt)}
        onChange={(event) => {
          const arrivalTime = event.target.value;
          const departureTime = compactRouteTime(draft.departureAt);
          const resolvedArrivalDate = arrivalCalendarDate(
            departureDayDate,
            arrivalDayDate,
            departureTime,
            arrivalTime,
          );
          const arrivalAt = arrivalTime && resolvedArrivalDate
            ? `${resolvedArrivalDate}T${arrivalTime}:00`
            : null;
          const anchorArrival = Boolean(arrivalAt && anchoredDurationMinutes !== null);
          const next = withCalculatedRouteDuration({
            ...draft,
            departureAt: anchorArrival ? null : draft.departureAt,
            arrivalAt,
            durationMinutes: anchorArrival ? anchoredDurationMinutes : draft.durationMinutes,
          });
          onChange(arrivalTime
            ? withEstimatedBusRouteTimes(next, departureDayDate, arrivalDayDate)
            : next);
        }}
      />
      <label htmlFor="itinerary-duration">时长（分钟）</label>
      <div className="itinerary-duration-field">
        <input
          id="itinerary-duration"
          type="number"
          min="0"
          readOnly={durationIsCalculated}
          aria-describedby={durationIsCalculated ? "itinerary-duration-hint" : undefined}
          value={draft.durationMinutes ?? ""}
          onChange={(event) => {
            if (durationIsCalculated) return;
            onChange({
              ...draft,
              durationMinutes: event.target.value ? Number(event.target.value) : null,
            });
          }}
        />
        {durationIsCalculated ? (
          <span id="itinerary-duration-hint" className="itinerary-duration-hint">
            已根据出发和到达时刻自动计算
          </span>
        ) : null}
      </div>
      {showDistance ? <>
        <label htmlFor="itinerary-distance">距离（公里）</label>
        <input
          id="itinerary-distance"
          type="number"
          min="0"
          step="0.1"
          value={draft.distanceKm ?? ""}
          onChange={(event) => onChange({ ...draft, distanceKm: event.target.value || null })}
        />
      </> : null}
      {draft.transportMode !== "walk" ? <>
      <label htmlFor="itinerary-route-description">路线说明</label>
      <Input.TextArea
        id="itinerary-route-description"
        value={draft.segmentDescription ?? ""}
        onChange={(value) => onChange({ ...draft, segmentDescription: value || null })}
        autoSize={{ minRows: 3, maxRows: 6 }}
      />
      <label htmlFor="itinerary-route-remark">路线备注</label>
      <Input.TextArea
        id="itinerary-route-remark"
        value={draft.remark ?? ""}
        onChange={(value) => onChange({ ...draft, remark: value || null })}
        autoSize={{ minRows: 2, maxRows: 5 }}
      />
      </> : null}
      {showConstraintWarning && warning ? (
        <Alert
          className="itinerary-route-constraint-alert"
          type="warning"
          showIcon
          content={warning}
        />
      ) : null}
    </div>
  );
}
